import { apiClient } from "../../utils/apiClient";
import type { BrowserPdfDocument } from "../../contexts/PdfManagementContext";
import type { FormTemplateData } from "./RequestTemplateEditor";
import type {
  SignaturePlace,
  PdfDocumentResponse,
  SignaturePlaceResponse,
  SignatureTemplateSigner,
  RequestTemplateResponse,
} from "@secure-caseworker/shared";

/**
 * Transform API signature place response to frontend SignaturePlace
 */
function transformSignaturePlace(
  sp: SignaturePlaceResponse,
  signers: SignatureTemplateSigner[]
): SignaturePlace {
  const signer = signers.find((s) => s.id === sp.template_signer_id);
  return {
    id: sp.id,
    type: sp.type,
    page: sp.page_number,
    x: parseFloat(sp.x),
    y: parseFloat(sp.y),
    width: parseFloat(sp.width),
    height: parseFloat(sp.height),
    template_signer_key: signer?.key ?? "",
    label: sp.label ?? undefined,
  };
}

/**
 * Load PDF file and create a blob URL for preview
 */
async function loadPdfPreviewUrl(fileId: string): Promise<string | undefined> {
  try {
    const response = await apiClient.downloadFileResponse(fileId);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error(`Failed to load PDF preview for file ${fileId}:`, error);
    return undefined;
  }
}

/**
 * Transform API PDF document response to BrowserPdfDocument with preview URL
 */
async function transformPdfDocument(
  doc: PdfDocumentResponse,
  signers: SignatureTemplateSigner[]
): Promise<BrowserPdfDocument> {
  const previewUrl = await loadPdfPreviewUrl(doc.id);

  return {
    id: doc.id,
    previewUrl,
    title: doc.original_filename,
    signaturePlaces: doc.signature_places.map((sp) =>
      transformSignaturePlace(sp, signers)
    ),
  };
}

/**
 * Build FormTemplateData from API response for editing an existing template.
 * Fetches PDF preview URLs and transforms all data to frontend format.
 */
export async function buildFormTemplateData(
  template: RequestTemplateResponse
): Promise<FormTemplateData> {
  // Transform PDF documents with preview URLs (parallel loading)
  const pdfDocuments = await Promise.all(
    (template.pdf_documents ?? []).map((doc) =>
      transformPdfDocument(doc, template.signature_template_signers)
    )
  );

  // Extract signer roles from signature template signers
  const signerRoles = template.signature_template_signers?.map((s) => ({
    id: s.id,
    key: s.key,
    label: s.label,
    signing_order: s.signing_order,
    required: s.required,
  }));

  return {
    type: template.type,
    title: template.title,
    description: template.description ?? "",
    fields: template.form_template_fields.map((f) => ({
      id: f.id,
      field_type: f.field_type,
      label: f.label,
      required: f.required,
      options: f.options as string[] | undefined,
      placeholder: "",
    })),
    pdfDocuments,
    signerRoles,
    routing: (template.routing as "parallel" | "sequential") ?? "parallel",
  };
}
